// frontend/src/ChatSettingsModal.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ProtectedElement from './ProtectedElement';
import axios from 'axios';
import Modal from 'react-modal';
import './ChatSettingsModal.css';

// --- Configuration ---
const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// --- Modal Styles Definition (desktop defaults) ---
const customModalStyles = { 
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
        maxWidth: '850px', width: '90%', maxHeight: '85vh', overflow: 'hidden', padding: '0',
        borderRadius: '8px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', border: 'none',
        background: '#fff', display: 'flex', flexDirection: 'column',
     },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 4000 },
};
// --- End Modal Styles ---

// Default tool structure for initialization
// const DEFAULT_TOOLS_STATE = {
//     relevantText: true, googleSearch: false, queryExternalDatabase: false, exploreKnowledgeGraph: true,
//     searchCollectionKnowledge: true, saveTextToFile: false, listFilesInDirectory: false, readFileContent: false,
//     analyticsEngine: false, queryKnowledgeBase: false, listAllMyFiles: false, image_annotation: false, generate_image: false, createDirectory: true,
// };

const DEFAULT_TOOLS_STATE = {
    // All tools OFF by default
    relevantText: false,
    googleSearch: false,
    queryExternalDatabase: false,
    exploreKnowledgeGraph: false,
    searchCollectionKnowledge: false,
    saveTextToFile: false,
    listFilesInDirectory: false,
    readFileContent: false,
    analyticsEngine: false,
    wordDocumentGenerator: false,
    daxCoder: false,
    queryKnowledgeBase: false,
    // listAllMyFiles: false,  // Disabled
    image_annotation: false,
    generate_image: false,
    createDirectory: false,
    deleteFile: false,
    renameFile: false,
    scrapeWebsite: false,
    getStockInformation: false,
    outlookAssistant: false,
};

const ChatSettingsModal = ({
    isOpen,
    onSave,
    onCancel,
    initialUseAgentAPI,
    initialLoopsBeforeHumanInput,
    initialSelectedTools,
    initialSelectedPipeline,
    initialSeeThoughtsAndActions,
    selectedPromptId,
    onSelectPrompt = () => {},
    authUsername,
}) => {
    // --- Responsive state ---
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 600px)');
        const update = () => setIsMobile(mq.matches);
        update();
        try { mq.addEventListener('change', update); } catch { mq.addListener(update); }
        return () => { try { mq.removeEventListener('change', update); } catch { mq.removeListener(update); } };
    }, []);

    function isPermissionDenied(error) {
        if (!error) return false;
        const status = error.response?.status;
        if (status === 401 || status === 403) return true;
        const msg = (error.response?.data?.error || error.response?.data?.message || error.message || '').toLowerCase();
        if (!msg) return false;
        if (msg.includes('permission') && msg.includes('denied')) return true;
        if (msg.includes('not authorized') || msg.includes('forbidden')) return true;
        return false;
    }
    const [activeTab, setActiveTab] = useState('Agent Settings');

    // --- Internal State for Modal Edits ---
    // Removed: RAG/Text2SQL settings and selections
    const [currentUseAgentAPI, setCurrentUseAgentAPI] = useState(initialUseAgentAPI ?? true);
    const [currentLoops, setCurrentLoops] = useState(initialLoopsBeforeHumanInput ?? 10);
    const [currentTools, setCurrentTools] = useState(initialSelectedTools ? { ...DEFAULT_TOOLS_STATE, ...initialSelectedTools } : { ...DEFAULT_TOOLS_STATE });
    const [currentPipeline, setCurrentPipeline] = useState(initialSelectedPipeline ?? '');
    const [currentSeeThoughts, setCurrentSeeThoughts] = useState(initialSeeThoughtsAndActions ?? false);

    // --- Baseline snapshot captured when the modal opens ---
    // Used when closing to preserve settings from the initial open.
    const baselineSettingsRef = useRef(null);

    const sanitizeToolsForSave = useCallback((tools) => {
        const next = tools ? { ...DEFAULT_TOOLS_STATE, ...tools } : { ...DEFAULT_TOOLS_STATE };
        delete next.FileTools;
        delete next.SearchScrapeTools;
        return next;
    }, []);

    // --- Data Fetching State ---
    // Removed Text2SQL database list; no longer used after removing LLM Settings
    const [prompts, setPrompts] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [selectedPromptDetails, setSelectedPromptDetails] = useState(null);
    const [showFullPromptText, setShowFullPromptText] = useState(false);
    const [loadingError, setLoadingError] = useState('');

    const fetchData = useCallback(async (endpoint, setter, name) => {
        if (!isOpen) return; 
        try {
            const response = await axios.get(`${BASE_URL}/api/${endpoint}`, { withCredentials: true });
            setter(response.data || []);
            console.log(`ChatSettingsModal: Fetched ${name}:`, response.data?.length);
        } catch (error) {
            console.error(`ChatSettingsModal: Error fetching ${name}:`, error);
            if (isPermissionDenied(error)) {
                console.info(`Permission denied fetching ${name} (suppressed).`);
            } else {
                let errorMsg = `Failed to load ${name}.`;
                if (error.response?.status === 401) errorMsg = `Failed to load ${name}: Unauthorized.`;
                else if (error.response?.status === 404) errorMsg = `Failed to load ${name}: Endpoint not found (/api/${endpoint}).`;
                else if (error.message) errorMsg = `Failed to load ${name}: ${error.message}.`;
                setLoadingError(prev => `${prev}\n${errorMsg}`.trim());
            }
        }
    }, [BASE_URL, isOpen]);

    const handleFileToolsGroupChange = (isChecked) => {
        console.info(`ChatSettingsModal: File Tools group state: ${isChecked ? 'All selected' : 'Not all selected'}`);
        setCurrentTools(prev => {
            const newTools = { ...prev };
            FILE_TOOLS_GROUP_KEYS.forEach(key => {
                newTools[key] = isChecked;
            });
            // Update the 'FileTools' meta-key itself to reflect the group state
            newTools.FileTools = isChecked; // This line ensures the internal state of 'FileTools' matches the group's intent
            return newTools;
        });
    };

    const handleSearchScrapeGroupChange = (isChecked) => {
        setCurrentTools(prev => {
            const newTools = { ...prev };
            SEARCH_SCRAPE_GROUP_KEYS.forEach(key => {
                newTools[key] = isChecked;
            });
            newTools.SearchScrapeTools = isChecked; // Update the meta-key
            return newTools;
        });
    };

    // Select/Clear all tools within a given group (including consolidated groups)
    const handleSelectAllGroup = (toolKeys) => {
        const expandedKeys = toolKeys.flatMap(key => {
            if (key === 'FileTools') return FILE_TOOLS_GROUP_KEYS;
            if (key === 'SearchScrapeTools') return SEARCH_SCRAPE_GROUP_KEYS;
            return [key];
        });
        const allSelected = expandedKeys.every(k => !!currentTools[k]);
        const target = !allSelected; // toggle: if all selected then clear; else select all
        setCurrentTools(prev => {
            const next = { ...prev };
            expandedKeys.forEach(k => { next[k] = target; });
            if (toolKeys.includes('FileTools')) next.FileTools = target;
            if (toolKeys.includes('SearchScrapeTools')) next.SearchScrapeTools = target;
            return next;
        });
    };

    useEffect(() => {
        if (isOpen) {
            console.log("ChatSettingsModal: Initializing/Re-initializing on open from props.");
            console.log("ChatSettingsModal: Initial Tools Received:", initialSelectedTools);
            console.log("ChatSettingsModal: Initial SeeThoughts Received:", initialSeeThoughtsAndActions);
            
            // RAG/Text2SQL removed
            // Defaults aligned with ChatWindow: Agent ON, loops = 10 when not provided
            setCurrentUseAgentAPI(initialUseAgentAPI ?? true);
            setCurrentLoops(initialLoopsBeforeHumanInput ?? 10);
            
            // Initialize currentTools correctly, merging with default states
            const mergedTools = initialSelectedTools
                ? { ...DEFAULT_TOOLS_STATE, ...initialSelectedTools }
                : { ...DEFAULT_TOOLS_STATE };
            // Ensure FileTools meta-key also reflects initial state of its group
            const areInitialFileToolsSelected = FILE_TOOLS_GROUP_KEYS.every(key => !!mergedTools[key]);
            mergedTools.FileTools = areInitialFileToolsSelected;

            // Ensure SearchScrapeTools meta-key also reflects initial state of its group
            const areInitialSearchScrapeToolsSelected = SEARCH_SCRAPE_GROUP_KEYS.every(key => !!mergedTools[key]);
            mergedTools.SearchScrapeTools = areInitialSearchScrapeToolsSelected;

            setCurrentTools(mergedTools);


            setCurrentPipeline(initialSelectedPipeline ?? '');
            setCurrentSeeThoughts(initialSeeThoughtsAndActions ?? false);

            // Capture a baseline snapshot as soon as we open.
            baselineSettingsRef.current = {
                useAgentAPI: initialUseAgentAPI ?? true,
                loopsBeforeHumanInput: initialLoopsBeforeHumanInput ?? 10,
                selectedTools: sanitizeToolsForSave(mergedTools),
                selectedPipeline: initialSelectedPipeline ?? '',
                seeThoughtsAndActions: initialSeeThoughtsAndActions ?? false,
            };

            setLoadingError('');
            setPrompts([]); setPipelines([]);
            setShowFullPromptText(false);

            fetchData('prompts', (data) => {
                setPrompts(data || []);
                // Set selectedPromptDetails from selectedPromptId if available
                if (selectedPromptId && Array.isArray(data)) {
                    const found = data.find(p => p.id?.toString() === selectedPromptId?.toString());
                    setSelectedPromptDetails(found || null);
                    // If both a prompt and a pipeline were provided initially, enforce exclusivity by dropping the pipeline
                    if (found && (initialSelectedPipeline ?? '') !== '') {
                        setCurrentPipeline('');
                    }
                } else {
                    setSelectedPromptDetails(null);
                }
            }, 'Prompts');
            fetchData('prompt-pipelines/get-pipelines', setPipelines, 'Pipelines');
        }
    }, [
        isOpen,
        initialUseAgentAPI, initialLoopsBeforeHumanInput, initialSelectedTools, initialSelectedPipeline,
        initialSeeThoughtsAndActions,
        fetchData,
        sanitizeToolsForSave
    ]);

    const handlePromptSelectChange = (event) => {
        const promptId = event.target.value;
        const selected = prompts.find(p => p.id?.toString() === promptId);
        setSelectedPromptDetails(selected || null);
        setShowFullPromptText(false);

        // If a prompt is explicitly selected, drop any selected pipeline
        if (selected && currentPipeline) {
            setCurrentPipeline('');
        }

        // Propagate selected prompt ID to parent
        if (selected) {
            if (selected.provider && selected.model) {
                onSelectPrompt({
                    id: selected.id,
                    name: selected.prompt_name, text: selected.prompt_text || '',
                    provider: selected.provider, model: selected.model,
                    temperature: selected.temperature, max_tokens: selected.max_tokens,
                    source: 'prompt_specific_llm_settings'
                });
            } else {
                onSelectPrompt({ id: selected.id, name: selected.prompt_name, text: selected.prompt_text || '', source: 'prompt_text_only' });
            }
        } else {
            onSelectPrompt({ id: null, name: null, text: '', source: 'default_prompt' });
        }
    };

    const handlePipelineSelectChange = (event) => {
        const value = event.target.value;
        setCurrentPipeline(value);
        if (value) {
            // Selecting a pipeline drops any selected system prompt
            setSelectedPromptDetails(null);
            setShowFullPromptText(false);
            // Inform parent that we reverted to default prompt (since pipeline is selected)
            onSelectPrompt({ id: null, name: null, text: '', source: 'default_prompt' });
        }
    };

    const handleToolChange = (toolKey) => {
        setCurrentTools(prev => {
            const newState = { ...prev, [toolKey]: !prev[toolKey] };

            // Logic to update the "File Tools" group checkbox based on individual tool states
            if (FILE_TOOLS_GROUP_KEYS.includes(toolKey)) {
                const allFileToolsChecked = FILE_TOOLS_GROUP_KEYS.every(key => !!newState[key]);
                newState.FileTools = allFileToolsChecked;
            }

            // Logic to update the "Search & Scrape" group checkbox based on individual tool states
            if (SEARCH_SCRAPE_GROUP_KEYS.includes(toolKey)) {
                const allSearchScrapeToolsChecked = SEARCH_SCRAPE_GROUP_KEYS.every(key => !!newState[key]);
                newState.SearchScrapeTools = allSearchScrapeToolsChecked;
            }

            return newState;
        });
    };

    const renderPromptText = (text) => {
        console.log('ChatSettingsModal: Displaying selected prompt details:', selectedPromptDetails);
        if (!text) return '';
        const limit = 150;
        if (text.length <= limit) return text;
        if (showFullPromptText) return (<>{text}<button className="link-button" onClick={() => setShowFullPromptText(false)}>Less</button></>);
        else return (<>{`${text.substring(0, limit)}...`}<button className="link-button" onClick={() => setShowFullPromptText(true)}>More</button></>);
    };

    // Normalize agent_tools structure from selectedPromptDetails into a string array of enabled tool keys
    const normalizedAgentTools = useMemo(() => {
        const at = selectedPromptDetails?.agent_tools;
        if (!at) return [];
        if (Array.isArray(at)) return at.filter(Boolean);
        if (typeof at === 'object') {
            return Object.entries(at)
                .filter(([, enabled]) => !!enabled)
                .map(([key]) => key);
        }
        return [];
    }, [selectedPromptDetails]);

    const humanizeToolKey = (key) => {
        if (!key) return '';
        // Replace underscores with spaces first, then split CamelCase
        const spaced = key.toString().replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
        return spaced.replace(/^./, (s) => s.toUpperCase());
    };

    const isAgentActive = currentUseAgentAPI;
    // LLM tool toggles removed; only agent mode can be highlighted now

    const handleToggleChange = (settingName, isChecked) => {
        // Only agent workflow toggle remains
        if (settingName === 'useAgentAPI') {
            setCurrentUseAgentAPI(!!isChecked);
        }
    };

    const handleApplyAndCloseInternal = () => {
        let toolsToSave = { ...currentTools };
        delete toolsToSave.FileTools;
        delete toolsToSave.SearchScrapeTools;

        console.log('ChatSettingsModal: Apply & Close clicked. Calling onSave with current modal states.');
        onSave(
            currentLoops,
            toolsToSave,
            currentPipeline,
            currentUseAgentAPI,
            currentSeeThoughts
        );
    };

    const handleCancelAndCloseInternal = () => {
        console.log('ChatSettingsModal: Close/Cancel triggered (X, ESC, or Overlay). Calling onCancel.');
        onCancel();
    };


    const mainTabs = ['Prompt Settings', 'Agent Settings'];
    const activeModeMainTab = isAgentActive ? 'Agent Settings' : null;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onRequestClose={handleCancelAndCloseInternal}
                style={customModalStyles}
                contentLabel="Chat Settings Modal"
                ariaHideApp={false}
            >
                <div className="chat-settings-modal-header-layout">
                    <div className="chat-settings-modal-header">
                        <h2>Chat Settings</h2>
                        <button onClick={handleCancelAndCloseInternal} className="modal-close-button top-right" title="Close (Discard Changes)">×</button>
                    </div>
                    <div className="chat-settings-tabs-row">
                        <div className="chat-settings-tabs main-tabs">
                            {mainTabs.map((tab) => (
                                <button
                                    key={tab}
                                    className={`tab-button ${activeTab === tab ? 'active' : ''} ${activeModeMainTab === tab ? 'active-mode' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {loadingError && ( <div className="modal-error" style={{ whiteSpace: 'pre-line' }}> {loadingError.trim()} </div> )}

                <div className="chat-settings-tab-content">
                    {activeTab === 'Prompt Settings' && (
                        <ProtectedElement required="chat:pro" fallbackVariant="overlay" forceFullHeight>
                            <>
                                <div className="settings-section">
                                    <h3>System Prompt</h3>
                                    <div className="setting-item select">
                                        <label htmlFor="promptSelectPromptTab">Select System Prompt:</label>
                                        <select id="promptSelectPromptTab" onChange={handlePromptSelectChange} value={selectedPromptDetails?.id || ''}>
                                            <option value="">-- Default Prompt --</option>
                                            {prompts.map(prompt => ( <option key={prompt.id} value={prompt.id}> {prompt.prompt_name} </option> ))}
                                        </select>
                                    </div>
                                    {selectedPromptDetails && (
                                        <div className="prompt-details">
                                            <h4>Selected: {selectedPromptDetails.prompt_name}</h4>
                                            <p className="prompt-text-display">{renderPromptText(selectedPromptDetails.prompt_text)}</p>
                                        </div>
                                    )}
                                    {!selectedPromptDetails && <p><small>Using default system prompt.</small></p>}
                                </div>
                                {normalizedAgentTools.length > 0 && (
                                    <div className="settings-section">
                                        <h3>Agent Tools in this Prompt</h3>
                                        <div className="prompt-tools-list" style={{ marginTop: '8px' }}>
                                            <ul style={{ paddingLeft: '18px', margin: '6px 0' }}>
                                                {normalizedAgentTools.map((toolKey, idx) => {
                                                    const label = humanizeToolKey(toolKey);
                                                    const descr = TOOL_DESCRIPTIONS[toolKey];
                                                    return (
                                                        <li key={`${toolKey}-${idx}`} title={descr || label} style={{ margin: '4px 0' }}>
                                                            <span style={{ fontWeight: 500 }}>{label}</span>
                                                            {descr && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>— {descr}</span>}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                                <div className="settings-section">
                                    <h3>Prompt Pipeline</h3>
                                    <div className="setting-item select">
                                        <label htmlFor="pipelineSelectPromptTab">Select Prompt Pipeline (Optional):</label>
                                        <select id="pipelineSelectPromptTab" value={currentPipeline} onChange={handlePipelineSelectChange}>
                                            <option value="">-- No Pipeline --</option>
                                            {(pipelines || []).map(pipeline => ( <option key={pipeline.id} value={pipeline.name}> {pipeline.name} </option> ))}
                                        </select>
                                    </div>
                                </div>
                            </>
                            <ProtectedElement required={["chat:pro", "user:update-settings"]} behavior="hide" disabledTooltip="Requires user:update-settings permission">
                                <button onClick={handleApplyAndCloseInternal} className="modal-button primary">Apply & Close</button>
                            </ProtectedElement>
                        </ProtectedElement>
                    )}
                    {activeTab === 'Agent Settings' && (
                        <ProtectedElement required="chat:agent-messages" fallbackVariant="overlay" forceFullHeight>
                            <div className ="settings-section">
                                <div className="settings-section agent-options-card">
                                    <h3>Agent Options</h3>
                                    <div className="agent-options-grid">
                                        <div className="setting-item checkbox toggle">
                                            <input id="useAgentApi" type="checkbox" checked={currentUseAgentAPI}
                                                onChange={e => handleToggleChange('useAgentAPI', e.target.checked)} />
                                            <label htmlFor="useAgentApi">Use Agentic Workflow</label>
                                        </div>
                                        <div className="setting-item checkbox toggle">
                                            <input id="seeThoughtsModal" type="checkbox" checked={currentSeeThoughts} onChange={e => setCurrentSeeThoughts(e.target.checked)} disabled={!currentUseAgentAPI} />
                                            <label htmlFor="seeThoughtsModal" title={!currentUseAgentAPI ? 'Enable "Use Agentic Workflow" first' : ''}>Show Agent Thoughts/Actions</label>
                                        </div>
                                        <div className="setting-item number-input compact">
                                            <label htmlFor="agentLoopsModal">Loops before Human Input</label>
                                            <input id="agentLoopsModal" type="number" min="1" value={currentLoops} onChange={e => setCurrentLoops(Math.max(1, parseInt(e.target.value, 10) || 1))} disabled={!currentUseAgentAPI} />
                                        </div>
                                    </div>
                                </div>
                                <div className="settings-section">
                                    <h3>Agent Tools (Active if "Use Agentic Workflow" is checked)</h3>
                                    <div className="tools-group tools-columns">
                                        {Object.entries(TOOL_GROUPS).map(([groupName, toolKeys]) => {
                                            // Compute selected/total counts (expand consolidated keys)
                                            const selectedCount = toolKeys.reduce((acc, key) => {
                                                if (key === 'FileTools') return acc + FILE_TOOLS_GROUP_KEYS.filter(k => !!currentTools[k]).length;
                                                if (key === 'SearchScrapeTools') return acc + SEARCH_SCRAPE_GROUP_KEYS.filter(k => !!currentTools[k]).length;
                                                return acc + (currentTools[key] ? 1 : 0);
                                            }, 0);
                                            const totalCount = toolKeys.reduce((acc, key) => {
                                                if (key === 'FileTools') return acc + FILE_TOOLS_GROUP_KEYS.length;
                                                if (key === 'SearchScrapeTools') return acc + SEARCH_SCRAPE_GROUP_KEYS.length;
                                                return acc + 1;
                                            }, 0);
                                            const isAll = selectedCount === totalCount && totalCount > 0;
                                            const shouldOpen = selectedCount > 0;
                                            const RenderedToolGrid = () => {
                                                return (
                                                    <details className="tools-panel" key={groupName} open={shouldOpen}>
                                                        <summary className="tools-panel-header">
                                                            <span className="tools-title">{groupName.charAt(0).toUpperCase() + groupName.slice(1)} tools</span>
                                                            <span className="tools-actions">
                                                                <span className="tools-count">{selectedCount}/{totalCount} selected</span>
                                                                <button
                                                                    type="button"
                                                                    className="select-all-btn"
                                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSelectAllGroup(toolKeys); }}
                                                                    title={isAll ? `Clear all ${groupName} tools` : `Select all ${groupName} tools`}
                                                                >
                                                                    {isAll ? 'Clear All' : 'Select All'}
                                                                </button>
                                                            </span>
                                                        </summary>
                                                        <div className="tools-grid">
                                                        {toolKeys.map(toolKey => {
                                                            // --- Special logic for the consolidated "File Tools" ---
                                                            if (toolKey === 'FileTools') {
                                                                const areAllFileToolsSelected = FILE_TOOLS_GROUP_KEYS.every(key => !!currentTools[key]);
                                                                console.info(`ChatSettingsModal: File Tools group state: ${areAllFileToolsSelected ? 'All selected' : 'Not all selected'}`);
                                                                return (
                                                                    <div className="setting-item checkbox tool-item" key={toolKey}>
                                                                        <input
                                                                            id={`tool-${toolKey}`}
                                                                            type="checkbox"
                                                                            checked={areAllFileToolsSelected} // Reflects if ALL underlying are checked
                                                                            onChange={(e) => handleFileToolsGroupChange(e.target.checked)}
                                                                            disabled={!currentUseAgentAPI}
                                                                        />
                                                                        <label htmlFor={`tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}>
                                                                            {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                                        </label>
                                                                    </div>
                                                                );
                                                            }
                                                            // --- Special logic for the consolidated "Search & Scrape" Tools ---
                                                            if (toolKey === 'SearchScrapeTools') {
                                                                const areAllSearchScrapeToolsSelected = SEARCH_SCRAPE_GROUP_KEYS.every(key => !!currentTools[key]);
                                                                return (
                                                                    <div className="setting-item checkbox tool-item" key={toolKey}>
                                                                        <input
                                                                            id={`tool-${toolKey}`}
                                                                            type="checkbox"
                                                                            checked={areAllSearchScrapeToolsSelected}
                                                                            onChange={(e) => handleSearchScrapeGroupChange(e.target.checked)}
                                                                            disabled={!currentUseAgentAPI}
                                                                        />
                                                                        <label htmlFor={`tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}>
                                                                            {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                                        </label>
                                                                    </div>
                                                                );
                                                            }
                                                            
                                                            
                                                            // --- Default rendering for all other individual tools ---
                                                            return (
                                                                <div className="setting-item checkbox tool-item" key={toolKey}>
                                                                    <input
                                                                        id={`tool-${toolKey}`}
                                                                        type="checkbox"
                                                                        checked={!!currentTools[toolKey]}
                                                                        onChange={() => handleToolChange(toolKey)}
                                                                        disabled={!currentUseAgentAPI}
                                                                    />
                                                                    <label htmlFor={`tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}>
                                                                        {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                        </div>
                                                    </details>
                                                );
                                            };
                                            if (groupName === 'integrations') {
                                                return (
                                                    <ProtectedElement key={groupName} required="tool:outlook" behavior="overlay" disabledTooltip="Requires tools permission">
                                                        {RenderedToolGrid()}
                                                    </ProtectedElement>
                                                );
                                            }
                                            return <RenderedToolGrid />;
                                        }
                                        )}
                                    </div>
                                </div>
                                {/* Prompt selection moved to Prompt Settings; removed from Agent Settings */}
                            </div>
                            <ProtectedElement required={["chat:agent-messages", "user:update-settings"]} behavior="hide" disabledTooltip="Requires user:update-settings permission">
                                <button onClick={handleApplyAndCloseInternal} className="modal-button primary">Apply & Close</button>
                            </ProtectedElement>
                        </ProtectedElement>
                    )}
                </div>
                <div className="chat-settings-modal-actions">
                </div>
            </Modal>
        </>
    );
};

const FILE_TOOLS_GROUP_KEYS = ['saveTextToFile', 'readFileContent', 'listFilesInDirectory', 'createDirectory', 'deleteFile', 'renameFile'];

const SEARCH_SCRAPE_GROUP_KEYS = ['googleSearch', 'scrapeWebsite', 'getStockInformation'];

const TOOL_GROUPS = {
    retrieval: ['relevantText', 'exploreKnowledgeGraph', 'queryExternalDatabase', 'queryKnowledgeBase','listDocumentSections','retrieveDocumentSections'],
    'search & scrape': ['googleSearch', 'scrapeWebsite', 'getStockInformation'], // NEW GROUP
    file: ['FileTools'],
    media: ['image_annotation', 'generate_image'],
    analysis: ['analyticsEngine', "wordDocumentGenerator", "daxCoder"],
    integrations: ['outlookAssistant'],
};

const TOOL_DESCRIPTIONS = {
    FileTools: "A single option to enable all core file system operations: save, list, read, create directories, and list all personal files.", // New description for the meta-tool
    SearchScrapeTools: "A single option to enable all search and web scraping tools.", // New description for the meta-tool
    IntegrationTools: "Access external integrations such as Outlook to manage emails.",
    relevantText: "Return relevant text from articles in a collection based on user input.",
    searchCollectionKnowledge: "Search a knowledge graph collection for facts/statements using keywords.",
    collection_knowledge: "List graph facts/statements from a selected collection and use these to answer a question.",
    exploreKnowledgeGraph: "Explore a knowledge graph collection and synthesize an answer.",
    googleSearch: "Search Google for given keywords.",
    queryExternalDatabase: "Query a database for information based on your question.",
    saveTextToFile: "Save provided text to a file in the File Explorer.",
    listFilesInDirectory: "List files in a specified directory in the File Explorer.",
    readFileContent: "Read and return the content of a file in the File Explorer.",
    analyticsEngine: "Perform calculations or data analysis using Python code.",
    wordDocumentGenerator: "Generate professional word documents using python code.",
    daxCoder: "Generate DAX formulas for Power BI based on user queries and provided data context.",
    outlookAssistant: "Runs an Outlook specialist sub-agent to list/search emails, read a message by id, or create a draft email.",
    queryKnowledgeBase: "Query the Pearl Database for documents and information.",
    listDocumentSections: "List sections of a document in the Main database based on a document ID.",
    retrieveDocumentSections: "Retrieve specific sections of a document in the Main database based on a document ID and section IDs.",
    listAllMyFiles: "Provides a complete list of all file and directory paths within your personal Home workspace.",
    image_annotation: "Analyzes an image based on a query. Requires 'image_file_path' (relative to user's Home, e.g., 'Pictures/my_image.jpg') and 'query' (text prompt for the image). ex. {'image_file_path': 'Images/cat.jpg', 'query': 'What color is the cat?'}",
    generate_image: "Generates an image based on a detailed textual prompt using DALL-E. Saves the image to the user's Home/Images directory with a specified filename.",
    createDirectory: "Use to create a new directory inside the user's Home workspace. This can also create nested directories. Provide the name for the new directory. Example: {'directory_name': 'MyNewProject'}",
    deleteFile: "Delete a file or directory from the user's Home workspace. This action cannot be undone.",
    renameFile: "Rename a file or directory in the user's Home workspace. Provide the current path and the new name.",
    scrapeWebsite: "Use to scrape the text content from a given web URL. Expects a 'url' parameter. ex. {'url': 'https://en.wikipedia.org/wiki/Python_(programming_language)'}",
    getStockInformation: "Fetches historical stock market data for a list of one or more stock tickers from a specified start date. ex. {'tickers': ['AAPL', 'MSFT', 'NVDA'], 'start_date': '2025-07-07'}",
};

export default ChatSettingsModal;